import { StackScreenProps } from '@react-navigation/stack'
import {
  AppNavigation,
  AppNavigatorParams,
  ReplayNavigatorParams,
} from '../../../navigation/types'
import {
  TargetType,
  TimeLine,
  useGetTimelinePositionLazyQuery,
  useIsTargetAllowedQuery,
} from '../../../generated/graphql'
import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { ERROR_CODES, hasApolloErrorCode } from '../../../utils/error-utils'
import Toast from 'react-native-root-toast'
import { useTranslation } from 'react-i18next'
import { MessageType } from '../../../utils/message-type'
import { useAuthContext } from '../../../contexts/auth-context'
import { List, Text, ActivityIndicator } from 'react-native-paper'
import { FlatList, RefreshControl } from 'react-native-gesture-handler'
import { View, ViewToken } from 'react-native'
import { EmptyList } from '../../../@ui/containers/lists/empty-list'
import { UserLink } from '../../../@ui/typo/user.link'
import { IconEagleView, TimeLineIcon } from '../../../utils/meldd-icons'
import { useNavigation } from '@react-navigation/native'
import { createStyle } from '../../../contexts/global-styles.provider'
import { ScreenWrapper } from '../../../@ui/containers/grid/screen.wrapper'
import Row from '../../../@ui/containers/grid/row'
import { ReplayControls } from './components/replay-controllers'
import { ReplaySlider } from './components/replay.slider'
import {
  ITEMS_PER_PAGE,
  TimeLineItem,
  useTimeLine,
} from './components/use-timeline.hook'
import { useSnapshotStore } from '../../../services/snapshots/snapshot.store.hook'
import { useReplayStore } from '../../../services/snapshots/replay.store.hook'
import { TouchableIconWithToolTip } from '../../../utils/meldd-tooltip'
import { HoverableItem } from '../../../@ui/buttons/hooverable.area'
import { useTheme } from '../../../hooks/theme.hook'

const PayloadDescription = ({ item }: { item: TimeLine }) => {
  if (item.payload.originalText) {
    return (
      <Text variant="bodySmall" style={{ fontStyle: 'italic' }}>
        "<Text style={{}}>{item.payload.originalText}</Text>"{' → '}"
        {item.payload.title}"
      </Text>
    )
  }
  return (
    <Text variant="bodySmall" style={{ fontStyle: 'italic' }}>
      {item.payload.title}
    </Text>
  )
}

const LoadingItem = () => {
  const styles = useStyle()
  return (
    <List.Item
      title={<View style={{ height: 60 }} />}
      style={styles.listItem}
      description={
        <Row
          flex={1}
          justifyContent="center"
          alignItems="center"
          style={{ paddingBottom: 60 }}
        >
          <ActivityIndicator size="small" />
        </Row>
      }
    />
  )
}

const navigateTo = (
  targetId: string
): Record<
  TargetType,
  {
    screen: keyof ReplayNavigatorParams
    params: ReplayNavigatorParams[keyof ReplayNavigatorParams]
  }
> => {
  const config = {
    [TargetType.Node]: {
      screen: 'ReplayClusterList' as const,
      params: {},
    },
    [TargetType.Article]: {
      screen: 'ReplayPerspective' as const,
      params: {
        articleId: targetId,
      },
    },
    [TargetType.Cluster]: {
      screen: 'ReplayClusterCards' as const,
      params: {
        clusterId: targetId,
      },
    },
    [TargetType.Contribution]: {
      screen: 'ReplayContribution' as const,
      params: {
        contributionId: targetId,
      },
    },
  }
  return config
}

export const TimelineTimestamp = ({ item }: { item: TimeLine }) => {
  if (!item) {
    return ''
  }
  return new Date(item.timestamp).toLocaleString()
}

export const ReplyActionTitle = ({ item }: { item: TimeLine }) => {
  const { t } = useTranslation(['eagle-view', 'common'])
  if (!item) {
    return ''
  }

  return t(`action.${item.action}`, {
    type: t(`action.${item.payload.type}`),
  })
}

const ReviewItem = ({
  item,

  onClick,
}: {
  item: TimeLine & { chunk: number }
  onClick: (item: TimeLine) => void
}) => {
  const { t } = useTranslation(['eagle-view', 'common'])
  const [hoover, setHoover] = useState(false)
  const timestamp = useMemo(() => {
    if (!item) {
      return ''
    }
    return new Date(item.timestamp).toLocaleString()
  }, [item?.timestamp])

  if (!item) {
    return null
  }
  return (
    <HoverableItem onHover={(isHovered) => setHoover(isHovered)}>
      <List.Item
        style={{
          paddingHorizontal: 4,
          backgroundColor: hoover
            ? 'rgba(100, 149, 237, 0.25)' // When pressed/touched
            : 'transparent',
        }}
        title={
          <Text variant="labelSmall">{<ReplyActionTitle item={item} />}</Text>
        }
        left={() => (
          <View style={{ marginTop: 4 }}>
            <TimeLineIcon item={item} size={14} />
          </View>
        )}
        right={() => (
          <View style={{ flexDirection: 'column', alignItems: 'flex-end' }}>
            {item.user ? (
              <UserLink user={item.user} />
            ) : (
              <Text>{t('anonymous')}</Text>
            )}
            <Text variant="bodySmall" style={{ marginLeft: 8, paddingTop: 8 }}>
              {timestamp}
            </Text>
          </View>
        )}
        description={
          <View style={{ paddingTop: 8 }}>
            <PayloadDescription item={item} />
          </View>
        }
        onPress={() => {
          onClick(item)
        }}
      />
    </HoverableItem>
  )
}

/**
 * Hook to handle lazy loading of FlatList items when they become visible
 */
const useVisibleItemsLoader = (loadItemCallback: (index: number) => void) => {
  const [visibleItems, setVisibleItems] = useState<number[]>([])

  // Keep a ref to the latest visibleItems state
  const visibleItemsRef = useRef<number[]>([])
  useEffect(() => {
    visibleItemsRef.current = visibleItems
  }, [visibleItems])

  // Config for viewability
  const viewabilityConfig = useRef({
    viewAreaCoveragePercentThreshold: 20,
  })

  // Allow us to load pending chunks when they are visible
  const onViewableItemsChangedRef = useRef(
    ({
      viewableItems,
    }: {
      viewableItems: ViewToken[]
      changed: ViewToken[]
    }) => {
      // Extract indices of loading items that are visible
      const visibleIndices = viewableItems
        .filter((viewToken) => {
          const item = viewToken.item as TimeLineItem
          return item && item.loading === true
        })
        .map((viewToken) => {
          const item = viewToken.item as { chunk: number; loading: boolean }
          return item.chunk
        })

      // Load chunks for newly visible loading items
      visibleIndices.forEach((index) => {
        if (!visibleItemsRef.current.includes(index)) {
          loadItemCallback(index)
        }
      })

      setVisibleItems(visibleIndices)
    }
  )

  return {
    viewabilityConfig: viewabilityConfig.current,
    onViewableItemsChanged: onViewableItemsChangedRef.current,
  }
}

const ReplayImplementation = ({
  targetId,
  targetType,
}: {
  targetId: string
  targetType: TargetType
}) => {
  const { t } = useTranslation('replay')
  const navigation = useNavigation<AppNavigation>()
  const { initialize: initializeSnapshot } = useSnapshotStore()
  const { initialize: initializeReplay } = useReplayStore()
  const [getTimelinePosition] = useGetTimelinePositionLazyQuery()
  const [loadingReplyStack, setLoadingReplyStack] = useState(false)
  const theme = useTheme()
  const { chunks, loading, loadChunk, loadingChunk, totalItems } = useTimeLine({
    targetId,
    targetType,
  })
  // State for replay mode
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackIndex, setPlaybackIndex] = useState(0)

  // Use our custom hook to manage visibility loading
  const { viewabilityConfig, onViewableItemsChanged } =
    useVisibleItemsLoader(loadChunk)

  const timelineItems = useMemo(() => chunks.flat(), [chunks])

  // Reference to FlatList to control scrolling
  const flatListRef = useRef<FlatList<TimeLineItem>>(null)

  // Filter items for playback mode - only show items up to current playback index
  const displayItems = useMemo(() => {
    // Enable to display all items when not in playback mode
    // if (!isPlaying) {
    //   return timelineItems // Show all items when not in playback mode
    // }

    const items = timelineItems.slice(
      Math.max(timelineItems.length - playbackIndex - 1, 0),
      timelineItems.length
    )
    //

    return items
  }, [timelineItems, isPlaying, playbackIndex, totalItems])

  // Playback controls
  const startPlayback = useCallback(() => {
    if (playbackIndex >= totalItems - 1) {
      setPlaybackIndex(0)
    }
    setIsPlaying(true)
  }, [playbackIndex])

  const stopPlayback = useCallback(() => {
    setIsPlaying(false)

    // When stopping playback, scroll to the first visible item
    // This prevents jarring movement when switching from playback to normal view
    if (!flatListRef.current || timelineItems.length === 0) {
      return
    }
    const firstVisibleIndex = Math.max(
      0,
      timelineItems.length - playbackIndex - 1
    )
    // Use a slight delay to ensure the scroll happens after state updates
    setTimeout(() => {
      flatListRef.current?.scrollToIndex({
        index: firstVisibleIndex,
        animated: true,
        viewPosition: 0.2, // Position slightly towards the top (0=top, 1=bottom, 0.5=middle)
      })
    }, 300)
  }, [playbackIndex, timelineItems])

  // Handle playback progression with interval
  useEffect(() => {
    if (!isPlaying) return

    // If a not loaded chunk is comming we loaded in advance so the user
    // feels is more smooth
    const loadingChunk = timelineItems
      .slice(timelineItems.length - playbackIndex - 10, timelineItems.length)
      .find((item) => item?.loading)

    // Advance preload
    if (loadingChunk) {
      loadChunk(loadingChunk.chunk)
    }

    // Advance to next item after delay
    const timer = setTimeout(() => {
      // In worst case we have to wait for the next item to be loaded
      if (displayItems[0].loading) {
        // The visibility hook will load the chunk, will flirk and continue
        // Hopeful the advance preload will avoid this to happen
        return
      }
      if (playbackIndex >= timelineItems.length - 1) {
        setIsPlaying(false)
        clearTimeout(timer)
      }
      setPlaybackIndex((prev) => prev + 1)
    }, 1000) // 1 second between items

    return () => clearTimeout(timer)
  }, [
    displayItems,
    isPlaying,
    playbackIndex,
    timelineItems,
    setIsPlaying,
    loadChunk,
    stopPlayback,
    setPlaybackIndex,
  ])

  const onSeek = useCallback(
    async (index: number) => {
      // Remember the start of the play is the bottom of the line
      const jumpToRealIndex = totalItems - index
      const jumpToChunk = Math.floor(jumpToRealIndex / ITEMS_PER_PAGE)

      // If the chunk isn't loaded yet, load it first
      if (chunks[jumpToChunk][0].loading) {
        chunks[jumpToChunk] = await loadChunk(jumpToChunk)
      }

      // Is the prev chunk loaded?
      if (chunks[Math.max(0, jumpToChunk - 1)][0].loading) {
        chunks[jumpToChunk - 1] = await loadChunk(jumpToChunk - 1)
      }

      const totalChunksSize = chunks.flat().length
      const scrollCurrentIndex =
        chunks.slice(0, jumpToChunk).reduce((index, chunk) => {
          return index + chunk.length
        }, 0) +
        (jumpToRealIndex % ITEMS_PER_PAGE)

      const safeIndex = Math.max(
        0,
        Math.min(scrollCurrentIndex, totalChunksSize)
      )

      setTimeout(() => {
        flatListRef.current?.scrollToIndex({
          index: safeIndex,
          animated: false,
          viewPosition: 0.2,
        })
      }, 200)
    },
    [totalItems, timelineItems, isPlaying, loadChunk, chunks]
  )
  const navigateToReplay = useCallback(
    async (item: TimeLine) => {
      setLoadingReplyStack(true)

      // If we are in root we can use the chunks and index.
      // Otherwise is a filter timeline and we need to refetch all the node events to rebuild the tree
      const inRoot = targetType === TargetType.Node
      const rootNode = item.tree.find((node) => node.target === TargetType.Node)
      if (!rootNode) {
        setLoadingReplyStack(false)
        return
      }
      const { data } = await getTimelinePosition({
        variables: { id: item.id },
      })

      const cachedChunks = inRoot
        ? [] //  May cause issues with the timeline chunks.chunks.map((chunk) => chunk.toReversed()).toReversed()
        : []

      const totalItems = data?.getTimelinePosition?.total || 0
      const index = data?.getTimelinePosition?.position || 0

      initializeReplay({
        nodeId: rootNode.id,
        chunks: cachedChunks,
        totalItems,
      })
      initializeSnapshot({
        nodeId: rootNode.id,
        tick: index,
      })

      if (!navigateTo(targetId)[targetType]) {
        console.error('Invalid target type', targetType)
        setLoadingReplyStack(false)

        return
      }
      navigation.navigate('Replay', navigateTo(targetId)[targetType])
      setLoadingReplyStack(false)
    },
    [initializeReplay, initializeSnapshot, chunks, totalItems, navigation]
  )
  return (
    <ScreenWrapper
      style={{ padding: theme.spacing(2) }}
      withScrollView={false}
      refreshControl={<RefreshControl refreshing={loading} />}
    >
      <Row
        style={{
          paddingHorizontal: 8,
          marginBottom: 8,
        }}
        justifyContent="space-between"
      >
        <ReplayControls
          isPlaying={isPlaying}
          onNext={() => {
            if (playbackIndex < totalItems) {
              setPlaybackIndex(playbackIndex + 1)
              !isPlaying && onSeek(playbackIndex + 1)
            }
          }}
          onPrevious={() => {
            if (playbackIndex > 0) {
              setPlaybackIndex(playbackIndex - 1)
              !isPlaying && onSeek(playbackIndex - 1)
            }
          }}
          onPlay={startPlayback}
          onPause={stopPlayback}
        />
        {timelineItems.length > 0 && (
          <View style={{ flex: 1 }}>
            <Row style={{ paddingHorizontal: 10 }}>
              <ReplaySlider
                progress={playbackIndex}
                onSeek={(index) => {
                  setPlaybackIndex(index)
                  !isPlaying && onSeek(index)
                }}
                totalSteps={totalItems}
                isLoading={loading}
              />
            </Row>
          </View>
        )}
        {loadingReplyStack ? (
          <ActivityIndicator size="small" style={{ marginLeft: 10 }} />
        ) : (
          <TouchableIconWithToolTip
            mode="text"
            tooltip={t('enter-preview')}
            icon={<IconEagleView size={24} />}
            onPress={() => {
              // Timeline is reversed as the current list is reversed
              const orderPalyIndex = totalItems - playbackIndex // -1 cos we start counting on 0 not on 1

              const realPosition = timelineItems
                .slice(0, orderPalyIndex)
                .reduce((acc, item) => {
                  return item?.loading ? acc - 15 + 1 : acc // Loading node is 1 but represent 15, so are missing 14 on the count
                }, orderPalyIndex)
              const item =
                timelineItems[realPosition] || timelineItems.slice(-1)[0]

              // Is strange we click in item that is not loaded
              if (!item || item.loading) {
                console.error('Invalid item', realPosition)
                return
              }
              navigateToReplay(item as TimeLine)
            }}
          />
        )}
      </Row>

      <FlatList
        ref={flatListRef}
        style={{ marginLeft: 4, flex: 1 }}
        ListEmptyComponent={
          <EmptyList message={t('no-data')} loading={loading} />
        }
        onEndReachedThreshold={0.2}
        refreshing={loading || loadingChunk}
        data={displayItems}
        viewabilityConfig={viewabilityConfig}
        onViewableItemsChanged={onViewableItemsChanged}
        renderItem={({ item, index }) => {
          if (!item) {
            return null
          }
          if ('action' in item) {
            return (
              <ReviewItem
                item={item as TimeLine & { chunk: number }}
                onClick={(item) => navigateToReplay(item)}
              />
            )
          }
          return <LoadingItem />
        }}
        scrollEnabled={!isPlaying} // Disable scrolling during playback
      />
    </ScreenWrapper>
  )
}

const useCheckPermissions = ({
  targetId,
  targetType,
}: {
  targetId: string
  targetType: TargetType
}) => {
  const { isAnonymous, openLoginModal } = useAuthContext()
  const navigation = useNavigation<AppNavigation>()
  const { t } = useTranslation('common')
  const { error } = useIsTargetAllowedQuery({
    variables: { input: { targetId, targetType } },
    skip: !targetId || !targetType,
  })

  useEffect(() => {
    if (!error) {
      return
    }

    // For some scenarios request login
    if (
      hasApolloErrorCode(error, [
        ERROR_CODES.METHOD_NOT_ALLOWED,
        ERROR_CODES.FORBIDDEN,
      ]) &&
      isAnonymous
    ) {
      openLoginModal(navigation)
      return
    }
    // Display the error
    Toast.show(
      t(error?.message || "We couldn't find your article"),
      MessageType.error
    )
  }, [targetId, targetType, error])
}
export function ReplayScreen({
  route,
}: StackScreenProps<AppNavigatorParams, 'ReplayList'>) {
  const styles = useStyle()
  const { targetId, targetType } = route.params
  useCheckPermissions({ targetId, targetType })

  /*
   * React Native reuses the page component,
   * causing a cache effect and displaying previous article content.
   *
   * This is a workaround to force a new component to be created when the articleId changes.
   */
  return (
    <View style={styles.pageContainer}>
      <ReplayImplementation
        key={targetId}
        targetId={targetId}
        targetType={targetType}
      />
    </View>
  )
}

const useStyle = createStyle(() => ({
  listItem: {
    paddingBottom: 0,
    paddingTop: 0,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e0e0e0',
  },
}))
